import React, { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { UserPlus, Mail, Lock, User, FileText, BookOpen, GalleryVerticalEnd } from "lucide-react";
import { SiGoogle } from "react-icons/si";
const GoogleIcon = SiGoogle as unknown as React.ComponentType<{ className?: string }>;

const Register: React.FC = () => {
  const { user, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    bio: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const header = document.querySelector("header");
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => {
      if (!header) return;
      if (mq.matches) header.classList.add("hidden");
      else header.classList.remove("hidden");
    };
    apply();
    mq.addEventListener("change", apply);
    return () => {
      mq.removeEventListener("change", apply);
      if (header) header.classList.remove("hidden");
    };
  }, []);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters long";
    }

    if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (formData.bio.length > 500) {
      newErrors.bio = "Biography cannot exceed 500 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const success = await register({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      bio: formData.bio || undefined,
    });

    if (success) {
      navigate("/login");
    }

    setLoading(false);
  };

  return (
    <>
      <div className="lg:hidden grid min-h-svh">
        <div className="flex flex-col gap-4 p-6 md:p-10">
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-6">
              <img src="/LogoBeatPost.png" alt="Beatpost" style={{ width: "93px", height: "85px" }} />
              <div className="w-full max-w-xs">
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-newspaper-900">Create an account</div>
                    <div className="text-newspaper-600 text-sm">
                      Enter your information below to create your account
                    </div>
                  </div>
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-newspaper-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className={`beat-input ${errors.username ? "border-red-300" : ""} rounded-lg`}
                      placeholder="John Doe"
                      required
                    />
                    {errors.username && <p className="text-red-600 text-xs mt-1">{errors.username}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-newspaper-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="beat-input rounded-lg"
                      placeholder="m@example.com"
                      required
                    />
                    <p className="text-xs text-newspaper-600 mt-1">We will not share your email with anyone else.</p>
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-newspaper-700 mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`beat-input ${errors.password ? "border-red-300" : ""} rounded-lg`}
                      required
                    />
                    <p className="text-xs text-newspaper-600 mt-1">Must be at least 6 characters long.</p>
                    {errors.password && <p className="text-red-600 text-xs mt-1">{errors.password}</p>}
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-newspaper-700 mb-2">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`beat-input ${errors.confirmPassword ? "border-red-300" : ""} rounded-lg`}
                      required
                    />
                    <p className="text-xs text-newspaper-600 mt-1">Please confirm your password.</p>
                    {errors.confirmPassword && <p className="text-red-600 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>
                  <div>
                    <label htmlFor="bio" className="block text-sm font-medium text-newspaper-700 mb-2">
                      Biography (optional)
                    </label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      className={`beat-input min-h-[80px] resize-none ${errors.bio ? "border-red-300" : ""} rounded-lg`}
                      placeholder="Tell us about yourself..."
                      maxLength={500}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {errors.bio && <p className="text-red-600 text-xs">{errors.bio}</p>}
                      <p className="text-xs text-newspaper-500 ml-auto">{formData.bio.length}/500</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full beat-button flex items-center justify-center space-x-2 py-3 rounded-lg"
                    >
                      {loading ? (
                        <div className="beat-spinner"></div>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4" />
                          <span>Create Account</span>
                        </>
                      )}
                    </button>
                    {/* Register with Google (mobile) */}
                    <button
                      type="button"
                      onClick={() => loginWithGoogle()}
                      className="RegisterWithGoogle w-full beat-button-secondary flex items-center justify-center space-x-2 py-3 rounded-lg"
                    >
                      <GoogleIcon className="w-4 h-4" />
                      <span>Sign up with Google</span>
                    </button>
                    <p className="px-6 text-center text-newspaper-600">
                      Already have an account?{" "}
                      <Link to="/login" className="underline underline-offset-4 text-newspaper-900">
                        Sign in
                      </Link>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block">
        <div className="beat-container max-w-md mx-auto">
          <div className="beat-card p-8 rounded-lg">
            <div className="text-center mb-8">
              <div className="flex justify-center items-center mb-4">
                <BookOpen className="w-12 h-12 text-newspaper-900" />
              </div>
              <h1 className="text-2xl font-bold text-newspaper-900 mb-2">Join Beatpost</h1>
              <p className="text-newspaper-600">Start your literary journey</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-newspaper-700 mb-2">
                  Username
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className={`beat-input pl-10 ${errors.username ? "border-red-300" : ""} rounded-lg`}
                    placeholder="your_username"
                    required
                  />
                  <User className="absolute left-3 top-2.5 w-4 h-4 text-newspaper-400" />
                </div>
                {errors.username && <p className="text-red-600 text-xs mt-1">{errors.username}</p>}
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-newspaper-700 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="beat-input pl-10 rounded-lg"
                    placeholder="your@email.com"
                    required
                  />
                  <Mail className="absolute left-3 top-2.5 w-4 h-4 text-newspaper-400" />
                </div>
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-newspaper-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`beat-input pl-10 ${errors.password ? "border-red-300" : ""} rounded-lg`}
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-newspaper-400" />
                </div>
                {errors.password && <p className="text-red-600 text-xs mt-1">{errors.password}</p>}
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-newspaper-700 mb-2">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`beat-input pl-10 ${errors.confirmPassword ? "border-red-300" : ""} rounded-lg`}
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-newspaper-400" />
                </div>
                {errors.confirmPassword && <p className="text-red-600 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>
              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-newspaper-700 mb-2">
                  Biography (optional)
                </label>
                <div className="relative">
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    className={`beat-input pl-10 min-h-[80px] resize-none ${
                      errors.bio ? "border-red-300" : ""
                    } rounded-lg`}
                    placeholder="Tell us about yourself..."
                    maxLength={500}
                  />
                  <FileText className="absolute left-3 top-2.5 w-4 h-4 text-newspaper-400" />
                </div>
                <div className="flex justify-between items-center mt-1">
                  {errors.bio && <p className="text-red-600 text-xs">{errors.bio}</p>}
                  <p className="text-xs text-newspaper-500 ml-auto">{formData.bio.length}/500</p>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full beat-button flex items-center justify-center space-x-2 py-3 rounded-lg"
              >
                {loading ? (
                  <div className="beat-spinner"></div>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>Create Account</span>
                  </>
                )}
              </button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-newspaper-600">
                Already have an account?{" "}
                <Link to="/login" className="text-newspaper-900 font-medium hover:underline">
                  Sign in here
                </Link>
              </p>
            </div>
            {/* Register with Google Button */}
            <div className="mt-6 RegisterWithGoogle">
              <button
                type="button"
                onClick={() => loginWithGoogle()}
                className="w-full beat-button-secondary flex items-center justify-center space-x-2 py-3 rounded-lg"
              >
                <GoogleIcon className="w-4 h-4" />
                <span>Sign up with Google</span>
              </button>
            </div>
            <div className="mt-6 p-4 bg-newspaper-100 border border-newspaper-200 rounded-lg">
              <p className="text-xs text-newspaper-600">
                By creating an account, you agree to our terms of use and privacy policy. Beatpost is a platform for
                quality content and mutual respect.
              </p>
            </div>
            <div className="mt-6 text-center">
              <div className="beat-quote text-sm">"Be mad and too much, and create a new world"</div>
              <p className="text-xs text-newspaper-500 mt-2">— Allen Ginsberg</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
